'use client';

import React, { useState, useEffect, Suspense } from 'react';
import { useParams } from 'react-router-dom';
import { articlesApi } from '@/app/api/articles';
import { MarkdownRenderer } from '@/components/markdown/MarkdownRenderer';
import { geoUtils, GeoArticleData } from '@/utils/geoUtils';

function PublicArticleContent() {
  const { uuid } = useParams<{ uuid: string }>();

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [authorName, setAuthorName] = useState('');
  const [publishedAt, setPublishedAt] = useState<number | null>(null);
  const [summary, setSummary] = useState<string | null>(null);
  const [geoData, setGeoData] = useState<GeoArticleData | null>(null);
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!uuid) return;

    const fetchArticleContent = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await articlesApi.getPublicArticle(uuid!);
        setTitle(data.title);
        setContent(data.content);
        setAuthorName(data.authorName);
        setPublishedAt(data.publishedAt);
        setSummary(data.summary);

        // Prepare GEO Data
        setGeoData({
          title: data.title,
          summary: data.summary || '',
          authorName: data.authorName,
          authorJobTitle: data.authorJobTitle,
          authorBio: data.authorBio,
          authorSocialLinks: data.authorSocialLinks,
          publishedAt: new Date(data.publishedAt * 1000).toISOString(),
          // Advanced GEO
          publisherName: data.publisherName,
          publisherLogo: data.publisherLogo,
          citations: data.citations,
          mentions: data.mentions,
          canonicalUrl: window.location.href // Best guess for client-side
        });

        document.title = data.title; // Set the browser tab title
      } catch (err: any) {
        console.error('Error fetching public article:', err);
        setError(err.response?.data?.message || 'Article not found or failed to load.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchArticleContent();
  }, [uuid]);

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow-lg rounded-lg overflow-hidden">
          <div className="p-6 sm:p-8">
            {isLoading ? (
              <div className="text-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
                <p className="mt-4 text-lg text-gray-600">Loading article...</p>
              </div>
            ) : error ? (
              <div className="text-center py-20">
                <p className="text-2xl font-semibold text-red-600">Error</p>
                <p className="mt-2 text-gray-600">{error}</p>
              </div>
            ) : (
              <article className="prose lg:prose-xl max-w-none">
                <h1>{title}</h1>
                {authorName && publishedAt && (
                  <div className="flex justify-between items-center mb-8 text-gray-500 text-lg">
                    <div>
                      <span>By {authorName}</span>
                      <span className="mx-2">•</span>
                      <span>{formatDate(publishedAt)}</span>
                    </div>
                    {summary && (
                      <button
                        onClick={() => setIsSummaryExpanded(!isSummaryExpanded)}
                        className="text-sm text-blue-600 hover:text-blue-800 focus:outline-none"
                      >
                        {isSummaryExpanded ? 'Hide' : 'Show'} AI Summary
                      </button>
                    )}
                  </div>
                )}
                {isSummaryExpanded && summary && (
                  <div className="mb-8 p-4 bg-gray-100 rounded-lg border border-gray-200">
                    <p className="italic text-gray-700">{summary}</p>
                  </div>
                )}
                <MarkdownRenderer content={content} justified={true} />
              </article>
            )}

            {/* Inject GEO Schema */}
            {geoData && (
              <div dangerouslySetInnerHTML={{ __html: geoUtils.generateArticleSchema(geoData) }} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const formatDate = (timestamp: number) => {
  const date = new Date(timestamp * 1000);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

export default function PublicArticlePage() {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <PublicArticleContent />
    </Suspense>
  );
}
